import { Component, OnInit, ElementRef, ViewChild } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import Swal from 'sweetalert2';

import { SousBien } from 'app/models/sousBien';
import { SousBienService } from 'app/services/sousBiens/sous-bien.service';
import { AwscontrolService, ImageUploadResponse } from 'app/services/aws/awscontrol.service';

type MainUploadMode = 'direct' | 'imagesousproduit';
type GalleryUploadMode = 'direct' | 'imagesousproduit';

@Component({
  selector: 'app-modifier-sous-immob',
  templateUrl: './modifier-sous-immob.component.html',
  styleUrls: ['./modifier-sous-immob.component.scss']
})
export class ModifierSousImmobComponent implements OnInit {
  @ViewChild('descriptionTextarea', { static: false }) descriptionTextarea!: ElementRef;
  @ViewChild('descriptionArabeTextarea', { static: false }) descriptionArabeTextarea!: ElementRef;
  @ViewChild('descriptionAnglaisTextarea', { static: false }) descriptionAnglaisTextarea!: ElementRef;
  @ViewChild('mainImageInput', { static: false }) mainImageInput!: ElementRef;
  @ViewChild('imagesListInput', { static: false }) imagesListInput!: ElementRef;

  public sousBiensadd: any;
  public idBien: any;
  public parentBienId: string | null = null;
  public sousBien: SousBien;
  public selectedFiles: FileList | null = null;

  // ====== Language flags ======
  public availableLanguages: string[] = [];
  public showArabicFields: boolean = false;
  public showEnglishFields: boolean = false;

  // Images
  public allImages: string[] = [];
  public mainImage: string = '';
  public originalMainImage: string = '';
  public originalImagesList: string[] = [];

  // ==================== MODE + PREVIEW ====================
  mainUploadMode: MainUploadMode = 'direct';
  mainPreviewOriginalUrl: string | null = null;
  mainPreviewProcessedUrl: string | null = null;
  isAwaitingMainValidation: boolean = false;
  uploadingMain: boolean = false;

  galleryUploadMode: GalleryUploadMode = 'direct';
  galleryPreviewOriginalUrls: string[] = [];
  galleryPreviewProcessedUrls: string[] = [];
  isAwaitingGalleryValidation: boolean = false;
  uploadingGallery: boolean = false;
  // ========================================================

  // Loading states
  public isLoadingData: boolean = false;
  public isUploading: boolean = false;
  public isSubmitting: boolean = false;
  public isDeletingImage: boolean = false;

  dataSource = new MatTableDataSource();

  constructor(
    private sousBienService: SousBienService,
    private awsService: AwscontrolService,
    private router: Router,
    private _location: Location,
    private activatedRoute: ActivatedRoute
  ) {
    this.sousBien = new SousBien();
    this.sousBien.description = '';
    this.sousBien.descriptionArabe = '';
    this.sousBien.descriptionAnglais = '';
  }

  ngOnInit(): void {
    this.getData();
    this.checkAwsConnection();
  }

  checkAwsConnection(): void {
    this.awsService.checkHealth().subscribe({
      next: (response) => {
        console.log('✅ AWS S3 Connection Healthy:', response);
      },
      error: (err) => {
        console.warn('⚠️ AWS S3 Connection Warning:', err);
      }
    });
  }

  getData(): void {
    this.idBien = this.activatedRoute.snapshot.paramMap.get('id');

    if (!this.idBien) {
      Swal.fire({
        icon: 'error',
        title: 'Erreur',
        text: 'ID du sous bien non trouvé',
        confirmButtonColor: '#f44336'
      }).then(() => {
        this._location.back();
      });
      return;
    }

    this.isLoadingData = true;

    this.sousBienService.getSousBien(this.idBien).subscribe(
      data => {
        console.log('SousBien data loaded:', data);
        this.sousBien = data;

        const dataAny: any = data;

        if (dataAny.bienId) {
          this.parentBienId = dataAny.bienId.toString();
        } else if (dataAny.bien && dataAny.bien.id) {
          this.parentBienId = dataAny.bien.id.toString();
        } else if (dataAny.produit_id) {
          this.parentBienId = dataAny.produit_id.toString();
        }

        this.availableLanguages =
          dataAny.bien?.user?.availableLanguages ||
          dataAny.user?.availableLanguages ||
          dataAny.availableLanguages ||
          [];

        this.showArabicFields = this.availableLanguages.includes('AR');
        this.showEnglishFields = this.availableLanguages.includes('EN');

        if (dataAny.image_url) {
          this.mainImage = dataAny.image_url;
          this.originalMainImage = dataAny.image_url;
        }

        if (dataAny.imagesList && Array.isArray(dataAny.imagesList)) {
          this.allImages = [...dataAny.imagesList];
          this.originalImagesList = [...dataAny.imagesList];
        } else if (dataAny.images && Array.isArray(dataAny.images)) {
          this.allImages = [...dataAny.images];
          this.originalImagesList = [...dataAny.images];
        }

        if (dataAny.nameArabe) this.sousBien.nameArabe = dataAny.nameArabe;
        if (dataAny.nameAnglais) this.sousBien.nameAnglais = dataAny.nameAnglais;
        if (dataAny.descriptionArabe) this.sousBien.descriptionArabe = dataAny.descriptionArabe;
        if (dataAny.descriptionAnglais) this.sousBien.descriptionAnglais = dataAny.descriptionAnglais;
        if (dataAny.adressmap) this.sousBien.adressmap = dataAny.adressmap;

        console.log('Images loaded - Main:', this.mainImage, 'List:', this.allImages);
        console.log('Parent Bien ID:', this.parentBienId);
        console.log('Languages:', this.availableLanguages, 'showAR:', this.showArabicFields, 'showEN:', this.showEnglishFields);
        this.isLoadingData = false;
      },
      error => {
        console.error('Error loading SousBien:', error);
        this.isLoadingData = false;
        Swal.fire({
          icon: 'error',
          title: 'Erreur de chargement',
          text: 'Impossible de charger les données du sous bien',
          confirmButtonColor: '#f44336'
        }).then(() => {
          this._location.back();
        });
      }
    );
  }

  // ==================== TEXT FORMATTING ====================

  formatText(command: string): void {
    if (!this.descriptionTextarea) return;

    const textarea = this.descriptionTextarea.nativeElement;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = this.sousBien.description.substring(start, end);

    if (selectedText.length === 0) {
      Swal.fire({
        icon: 'info',
        title: 'Sélectionnez du texte',
        text: 'Veuillez sélectionner le texte que vous souhaitez formater.',
        timer: 2000,
        showConfirmButton: false
      });
      return;
    }

    let formattedText = '';
    switch (command) {
      case 'bold':      formattedText = `<strong>${selectedText}</strong>`; break;
      case 'italic':    formattedText = `<em>${selectedText}</em>`; break;
      case 'underline': formattedText = `<u>${selectedText}</u>`; break;
    }

    const newText = this.sousBien.description.substring(0, start) +
                    formattedText +
                    this.sousBien.description.substring(end);

    this.sousBien.description = newText;

    setTimeout(() => {
      textarea.focus();
      const newPosition = start + formattedText.length;
      textarea.setSelectionRange(newPosition, newPosition);
    }, 0);
  }

  formatTextArabe(command: string): void {
    if (!this.descriptionArabeTextarea) return;

    const textarea = this.descriptionArabeTextarea.nativeElement;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = this.sousBien.descriptionArabe.substring(start, end);

    if (selectedText.length === 0) {
      Swal.fire({
        icon: 'info',
        title: 'Sélectionnez du texte',
        text: 'Veuillez sélectionner le texte que vous souhaitez formater.',
        timer: 2000,
        showConfirmButton: false
      });
      return;
    }

    let formattedText = '';
    switch (command) {
      case 'bold':      formattedText = `<strong>${selectedText}</strong>`; break;
      case 'italic':    formattedText = `<em>${selectedText}</em>`; break;
      case 'underline': formattedText = `<u>${selectedText}</u>`; break;
    }

    const newText = this.sousBien.descriptionArabe.substring(0, start) +
                    formattedText +
                    this.sousBien.descriptionArabe.substring(end);

    this.sousBien.descriptionArabe = newText;

    setTimeout(() => {
      textarea.focus();
      const newPosition = start + formattedText.length;
      textarea.setSelectionRange(newPosition, newPosition);
    }, 0);
  }

  formatTextAnglais(command: string): void {
    if (!this.descriptionAnglaisTextarea) return;

    const textarea = this.descriptionAnglaisTextarea.nativeElement;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = this.sousBien.descriptionAnglais.substring(start, end);

    if (selectedText.length === 0) {
      Swal.fire({
        icon: 'info',
        title: 'Sélectionnez du texte',
        text: 'Veuillez sélectionner le texte que vous souhaitez formater.',
        timer: 2000,
        showConfirmButton: false
      });
      return;
    }

    let formattedText = '';
    switch (command) {
      case 'bold':      formattedText = `<strong>${selectedText}</strong>`; break;
      case 'italic':    formattedText = `<em>${selectedText}</em>`; break;
      case 'underline': formattedText = `<u>${selectedText}</u>`; break;
    }

    const newText = this.sousBien.descriptionAnglais.substring(0, start) +
                    formattedText +
                    this.sousBien.descriptionAnglais.substring(end);

    this.sousBien.descriptionAnglais = newText;

    setTimeout(() => {
      textarea.focus();
      const newPosition = start + formattedText.length;
      textarea.setSelectionRange(newPosition, newPosition);
    }, 0);
  }

  // ==================== HELPERS ====================
  private validateImageFile(file: File): { valid: boolean; error?: string } {
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];

    if (!validTypes.includes(file.type)) {
      return {
        valid: false,
        error: `Le fichier ${file.name} n'est pas une image valide. Formats acceptés: JPG, PNG, GIF, WebP.`
      };
    }

    if (file.size > 5 * 1024 * 1024) {
      return {
        valid: false,
        error: `L'image ${file.name} dépasse 5MB. Taille maximum: 5MB.`
      };
    }

    return { valid: true };
  }

  private fileToDataUrl(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e: any) => resolve(e.target.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  // ============================================================
  // ==================== MAIN IMAGE ============================
  // ============================================================

  setMainUploadMode(mode: MainUploadMode): void {
    if (this.uploadingMain) return;
    if (this.isAwaitingMainValidation) this.cancelMainPreview(true);
    this.mainUploadMode = mode;
  }

  selectFileimg(e: any): void {
    const input = e.target as HTMLInputElement;
    const files = input.files;
    if (!files || files.length === 0) return;

    const file = files[0];
    const validation = this.validateImageFile(file);

    if (!validation.valid) {
      Swal.fire({
        icon: 'warning',
        title: 'Fichier invalide',
        text: validation.error,
        confirmButtonColor: '#f44336'
      });
      input.value = '';
      return;
    }

    this.fileToDataUrl(file).then(d => { this.mainPreviewOriginalUrl = d; });

    if (this.mainUploadMode === 'direct') {
      this.uploadMainDirect(file, input);
    } else {
      this.uploadMainViaImagesousproduit(file, input);
    }
  }

  private uploadMainDirect(file: File, input: HTMLInputElement): void {
    this.uploadingMain = true;
    this.isUploading = true;

    this.awsService.uploadFile(file, `sous-bien-main/${this.idBien}`).subscribe({
      next: (response) => {
        this.uploadingMain = false;
        this.isUploading = false;

        if (response.success && response.urls && response.urls.length > 0) {
          const uploadedUrl = response.urls[0];
          console.log('✅ Main image uploaded:', uploadedUrl);
          this.mainImage = uploadedUrl;
          this.mainPreviewOriginalUrl = null;

          Swal.fire({
            icon: "success",
            title: "Image principale mise à jour",
            text: `${file.name} a été uploadée avec succès`,
            timer: 1500,
            showConfirmButton: false
          });
        } else {
          Swal.fire({
            icon: "error",
            title: "Échec de l'upload",
            text: response.error || `Échec du téléchargement de ${file.name}.`,
            confirmButtonColor: '#f44336'
          });
          this.mainPreviewOriginalUrl = null;
        }
        input.value = '';
      },
      error: (err) => {
        this.uploadingMain = false;
        this.isUploading = false;
        console.error('❌ Error uploading main image:', err);

        Swal.fire({
          icon: "error",
          title: "Échec de l'upload",
          text: `Échec du téléchargement de ${file.name}.`,
          confirmButtonColor: '#f44336'
        });
        this.mainPreviewOriginalUrl = null;
        input.value = '';
      }
    });
  }

  private uploadMainViaImagesousproduit(file: File, input: HTMLInputElement): void {
    this.uploadingMain = true;
    this.isAwaitingMainValidation = false;
    this.mainPreviewProcessedUrl = null;

    this.awsService.imagesousproduit([file], `sous-bien-main/${this.idBien}`).subscribe({
      next: (response: ImageUploadResponse) => {
        this.uploadingMain = false;
        if (response.success && response.urls && response.urls.length > 0) {
          this.mainPreviewProcessedUrl = response.urls[0];
          this.isAwaitingMainValidation = true;
        } else {
          Swal.fire({
            icon: 'error',
            title: 'Erreur',
            text: (response.errors && response.errors[0]) || 'Échec du traitement',
            confirmButtonColor: '#f44336'
          });
          this.mainPreviewOriginalUrl = null;
        }
        input.value = '';
      },
      error: () => {
        this.uploadingMain = false;
        Swal.fire({ icon: 'error', title: 'Erreur', text: 'Impossible de traiter l\'image principale', confirmButtonColor: '#f44336' });
        this.mainPreviewOriginalUrl = null;
        input.value = '';
      }
    });
  }

  validateMainPreview(): void {
    if (!this.mainPreviewProcessedUrl) return;
    this.mainImage = this.mainPreviewProcessedUrl;
    this.mainPreviewOriginalUrl = null;
    this.mainPreviewProcessedUrl = null;
    this.isAwaitingMainValidation = false;
    Swal.fire({ icon: 'success', title: 'Image principale validée', timer: 1500, showConfirmButton: false });
  }

  cancelMainPreview(silent: boolean = false): void {
    const url = this.mainPreviewProcessedUrl;
    const doCancel = () => {
      if (url) this.awsService.deleteFile(url).subscribe({ error: () => {} });
      this.mainPreviewOriginalUrl = null;
      this.mainPreviewProcessedUrl = null;
      this.isAwaitingMainValidation = false;
    };
    if (silent) { doCancel(); return; }
    Swal.fire({
      title: 'Refuser cette image ?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#f44336',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Oui, refuser',
      cancelButtonText: 'Annuler'
    }).then(r => { if (r.isConfirmed) doCancel(); });
  }

  removeMainImage(): void {
    if (this.isAwaitingMainValidation) { this.cancelMainPreview(); return; }
    if (!this.mainImage || this.mainImage.trim() === '') return;

    Swal.fire({
      title: 'Êtes-vous sûr?',
      text: "Voulez-vous vraiment supprimer l'image principale de AWS S3?",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#f44336',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Oui, supprimer!',
      cancelButtonText: 'Annuler'
    }).then((result) => {
      if (result.isConfirmed) {
        this.deleteImageFromS3(this.mainImage, -1, 'main');
      }
    });
  }

  // ============================================================
  // ==================== GALERIE ===============================
  // ============================================================

  setGalleryUploadMode(mode: GalleryUploadMode): void {
    if (this.uploadingGallery) return;
    if (this.isAwaitingGalleryValidation) this.cancelGalleryPreview(true);
    this.galleryUploadMode = mode;
  }

  selectFileimgListe(e: any): void {
    const input = e.target as HTMLInputElement;
    const files = input.files;
    if (!files || files.length === 0) return;

    const totalImages = this.allImages.length + files.length;
    if (totalImages > 30) {
      Swal.fire({
        icon: 'warning',
        title: 'Limite atteinte',
        text: `Maximum 30 images autorisées. Vous avez déjà ${this.allImages.length} image(s).`,
        confirmButtonColor: '#f44336'
      });
      input.value = '';
      return;
    }

    const validFiles: File[] = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const validation = this.validateImageFile(file);
      if (!validation.valid) {
        Swal.fire({ icon: 'warning', title: 'Fichier invalide', text: validation.error, confirmButtonColor: '#f44336' });
        continue;
      }
      validFiles.push(file);
    }

    if (validFiles.length === 0) { input.value = ''; return; }

    if (this.galleryUploadMode === 'direct') {
      this.uploadGalleryDirect(validFiles, input);
    } else {
      this.galleryPreviewOriginalUrls = [];
      validFiles.forEach(f => {
        this.fileToDataUrl(f).then(d => { this.galleryPreviewOriginalUrls.push(d); });
      });
      this.uploadGalleryViaImagesousproduit(validFiles, input);
    }
  }

  private uploadGalleryDirect(files: File[], input: HTMLInputElement): void {
    this.uploadingGallery = true;
    this.isUploading = true;

    this.awsService.uploadFiles(files, `sous-bien-images/${this.idBien}`).subscribe({
      next: (response) => {
        this.uploadingGallery = false;
        this.isUploading = false;

        if (response.success && response.urls && response.urls.length > 0) {
          this.allImages.push(...response.urls);

          Swal.fire({
            icon: "success",
            title: "Images ajoutées",
            text: `${response.urls.length} image(s) ajoutée(s) avec succès`,
            timer: 2000,
            showConfirmButton: false
          });
        } else {
          Swal.fire({
            icon: "error",
            title: "Échec de l'upload",
            text: response.error || 'Les images n\'ont pas pu être uploadées.',
            confirmButtonColor: '#f44336'
          });
        }
        input.value = '';
      },
      error: (err) => {
        this.uploadingGallery = false;
        this.isUploading = false;
        console.error('❌ Error uploading images:', err);

        Swal.fire({
          icon: "error",
          title: "Échec de l'upload",
          text: "Les images n'ont pas pu être uploadées.",
          confirmButtonColor: '#f44336'
        });
        input.value = '';
      }
    });
  }

  private uploadGalleryViaImagesousproduit(files: File[], input: HTMLInputElement): void {
    this.uploadingGallery = true;
    this.isAwaitingGalleryValidation = false;
    this.galleryPreviewProcessedUrls = [];

    this.awsService.imagesousproduit(files, `sous-bien-images/${this.idBien}`).subscribe({
      next: (response: ImageUploadResponse) => {
        this.uploadingGallery = false;
        if (response.success && response.urls && response.urls.length > 0) {
          this.galleryPreviewProcessedUrls = response.urls;
          this.isAwaitingGalleryValidation = true;
        } else {
          Swal.fire({
            icon: 'error',
            title: 'Erreur',
            text: (response.errors && response.errors[0]) || 'Échec du traitement',
            confirmButtonColor: '#f44336'
          });
          this.galleryPreviewOriginalUrls = [];
        }
        input.value = '';
      },
      error: () => {
        this.uploadingGallery = false;
        Swal.fire({ icon: 'error', title: 'Erreur', text: 'Impossible de traiter les images', confirmButtonColor: '#f44336' });
        this.galleryPreviewOriginalUrls = [];
        input.value = '';
      }
    });
  }

  validateGalleryPreview(): void {
    if (this.galleryPreviewProcessedUrls.length === 0) return;
    this.allImages.push(...this.galleryPreviewProcessedUrls);
    this.galleryPreviewOriginalUrls = [];
    this.galleryPreviewProcessedUrls = [];
    this.isAwaitingGalleryValidation = false;
    Swal.fire({ icon: 'success', title: 'Images validées', timer: 1500, showConfirmButton: false });
  }

  cancelGalleryPreview(silent: boolean = false): void {
    const urls = [...this.galleryPreviewProcessedUrls];
    const doCancel = () => {
      urls.forEach(u => this.awsService.deleteFile(u).subscribe({ error: () => {} }));
      this.galleryPreviewOriginalUrls = [];
      this.galleryPreviewProcessedUrls = [];
      this.isAwaitingGalleryValidation = false;
    };
    if (silent) { doCancel(); return; }
    Swal.fire({
      title: 'Refuser ces images ?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#f44336',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Oui, refuser',
      cancelButtonText: 'Annuler'
    }).then(r => { if (r.isConfirmed) doCancel(); });
  }

  // ==================== IMAGE MANAGEMENT ====================

  removeImage(index: number): void {
    const imageUrl = this.allImages[index];

    Swal.fire({
      title: 'Êtes-vous sûr?',
      text: "Voulez-vous vraiment supprimer cette image de AWS S3?",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#f44336',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Oui, supprimer!',
      cancelButtonText: 'Annuler'
    }).then((result) => {
      if (result.isConfirmed) {
        this.deleteImageFromS3(imageUrl, index, 'list');
      }
    });
  }

  private deleteImageFromS3(imageUrl: string, index: number, type: 'main' | 'list'): void {
    this.isDeletingImage = true;

    this.awsService.deleteFile(imageUrl).subscribe({
      next: (response) => {
        this.isDeletingImage = false;
        console.log('✅ Image deleted from S3:', response);

        if (type === 'list' && index >= 0) {
          this.allImages.splice(index, 1);
        } else if (type === 'main') {
          this.mainImage = '';
        }

        Swal.fire({
          icon: "success",
          title: "Image supprimée",
          text: "L'image a été supprimée de AWS S3",
          timer: 1500,
          showConfirmButton: false
        });
      },
      error: (err) => {
        this.isDeletingImage = false;
        console.error('❌ Error deleting image:', err);

        Swal.fire({
          icon: "error",
          title: "Échec de la suppression",
          text: "Impossible de supprimer l'image de AWS S3",
          confirmButtonColor: '#f44336'
        });
      }
    });
  }

  moveImageUp(index: number): void {
    if (index > 0) {
      const temp = this.allImages[index];
      this.allImages[index] = this.allImages[index - 1];
      this.allImages[index - 1] = temp;
    }
  }

  moveImageDown(index: number): void {
    if (index < this.allImages.length - 1) {
      const temp = this.allImages[index];
      this.allImages[index] = this.allImages[index + 1];
      this.allImages[index + 1] = temp;
    }
  }

  // ==================== VALIDATION ====================
  private validateForm(): { valid: boolean; error?: string } {
    if (this.isAwaitingMainValidation) {
      return { valid: false, error: "Veuillez valider ou refuser l'image principale." };
    }
    if (this.isAwaitingGalleryValidation) {
      return { valid: false, error: "Veuillez valider ou refuser les images de la galerie." };
    }
    if (!this.sousBien.name || this.sousBien.name.trim() === '') {
      return { valid: false, error: "Le nom est obligatoire." };
    }

    if (!this.sousBien.description || this.sousBien.description.trim() === '') {
      return { valid: false, error: "La description est obligatoire." };
    }

    if (!this.mainImage && !this.sousBien.image_url) {
      return { valid: false, error: "L'image principale est obligatoire." };
    }

    return { valid: true };
  }

  // ==================== CLEANUP ====================
  private async cleanupUnusedImages(): Promise<void> {
    const promises: Promise<void>[] = [];

    if (this.originalMainImage &&
        this.originalMainImage !== this.mainImage &&
        this.originalMainImage.trim() !== '') {
      promises.push(this.deleteImagePromise(this.originalMainImage));
    }

    const removedImages = this.originalImagesList.filter(
      originalImage => !this.allImages.includes(originalImage)
    );

    removedImages.forEach(imageUrl => {
      if (imageUrl && imageUrl.trim() !== '') {
        promises.push(this.deleteImagePromise(imageUrl));
      }
    });

    try {
      await Promise.all(promises);
      console.log('✅ Cleaned up unused images');
    } catch (error) {
      console.warn('⚠️ Some images could not be cleaned up:', error);
    }
  }

  private deleteImagePromise(imageUrl: string): Promise<void> {
    return new Promise((resolve) => {
      this.awsService.deleteFile(imageUrl).subscribe({
        next: () => resolve(),
        error: (err) => {
          console.warn('Could not delete image:', imageUrl, err);
          resolve();
        }
      });
    });
  }

  // ==================== UPDATE ====================
  updateSousImmob(): void {
    const validation = this.validateForm();

    if (!validation.valid) {
      Swal.fire({
        icon: "warning",
        title: "Informations manquantes",
        text: validation.error,
        confirmButtonColor: '#f44336'
      });
      return;
    }

    this.cleanupUnusedImages().then(() => {
      this.sousBiensadd = {
        name: this.sousBien.name.trim(),
        nameArabe: this.showArabicFields ? (this.sousBien.nameArabe || '') : '',
        nameAnglais: this.showEnglishFields ? (this.sousBien.nameAnglais || '') : '',
        category: this.sousBien.category || '',
        description: this.sousBien.description.trim(),
        descriptionArabe: this.showArabicFields ? (this.sousBien.descriptionArabe || '') : '',
        descriptionAnglais: this.showEnglishFields ? (this.sousBien.descriptionAnglais || '') : '',
        adressmap: this.sousBien.adressmap || '',
        image_url: this.mainImage || this.sousBien.image_url,
        imagesList: this.allImages,
        prix: this.sousBien.prix || ''
      };

      console.log('Updating with data:', this.sousBiensadd);

      this.isSubmitting = true;

      Swal.fire({
        title: 'Mise à jour en cours...',
        text: 'Veuillez patienter',
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      this.sousBienService.updateSousBien(this.idBien, this.sousBiensadd).subscribe(
        (response: any) => {
          this.isSubmitting = false;
          Swal.close();
          console.log('SousBien updated successfully:', response);

          this.originalMainImage = this.mainImage;
          this.originalImagesList = [...this.allImages];

          Swal.fire({
            icon: "success",
            title: "Succès",
            text: "Le sous bien a été mis à jour avec succès!",
            confirmButtonColor: '#4caf50'
          }).then(() => {
            this.goToParentList();
          });

          this.dataSource.data = this.dataSource.data.map((o: SousBien) => {
            if (o.id === response.id) {
              o = response;
            }
            return o;
          });
        },
        (error) => {
          this.isSubmitting = false;
          Swal.close();
          console.error('Error updating SousBien:', error);

          let errorMessage = 'Échec de la mise à jour du sous bien. Veuillez réessayer.';

          if (error.error) {
            if (typeof error.error === 'string') {
              errorMessage = error.error;
            } else if (error.error.message) {
              errorMessage = error.error.message;
            }
          } else if (error.message) {
            errorMessage = error.message;
          }

          Swal.fire({
            icon: "error",
            title: "Échec de la mise à jour",
            text: errorMessage,
            confirmButtonColor: '#f44336'
          });
        }
      );
    });
  }

  // ==================== NAVIGATION ====================
  private goToParentList(): void {
    if (this.parentBienId) {
      this.router.navigate(['/list-agence-sous-bien', this.parentBienId]);
    } else {
      this._location.back();
    }
  }

  goBack(): void {
    this.goToParentList();
  }

  resetForm(): void {
    Swal.fire({
      title: 'Réinitialiser les modifications?',
      text: 'Toutes les modifications non sauvegardées seront perdues',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#f44336',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Oui, réinitialiser',
      cancelButtonText: 'Annuler'
    }).then((result) => {
      if (result.isConfirmed) {
        this.mainPreviewOriginalUrl = null;
        this.mainPreviewProcessedUrl = null;
        this.isAwaitingMainValidation = false;
        this.mainUploadMode = 'direct';
        this.galleryPreviewOriginalUrls = [];
        this.galleryPreviewProcessedUrls = [];
        this.isAwaitingGalleryValidation = false;
        this.galleryUploadMode = 'direct';

        this.getData();

        if (this.mainImageInput) {
          this.mainImageInput.nativeElement.value = '';
        }
        if (this.imagesListInput) {
          this.imagesListInput.nativeElement.value = '';
        }

        Swal.fire({
          icon: 'success',
          title: 'Formulaire réinitialisé',
          text: 'Les modifications ont été annulées',
          timer: 1500,
          showConfirmButton: false
        });
      }
    });
  }

  getCurrentMainImage(): string {
    return this.mainImage || 'assets/images/default-image.png';
  }

  hasUnsavedChanges(): boolean {
    return (
      this.mainImage !== this.originalMainImage ||
      JSON.stringify(this.allImages) !== JSON.stringify(this.originalImagesList)
    );
  }

  canDeactivate(): boolean {
    if (this.hasUnsavedChanges()) {
      return confirm('Vous avez des modifications non sauvegardées. Voulez-vous vraiment quitter?');
    }
    return true;
  }
}